<script setup lang="ts">
  import { CalendarDate, DateFormatter, getLocalTimeZone } from '@internationalized/date';
  import { useQuery, useQueryClient } from '@tanstack/vue-query';
  import type { Row } from '@tanstack/vue-table';
  import * as changeCase from 'change-case';
  import dayjs from 'dayjs';
  import type { DateRange, DateValue } from 'reka-ui';
  import { computed, inject, onBeforeMount, onBeforeUnmount, ref } from 'vue';
  import type { Ref } from 'vue';
  import { useRoute } from 'vue-router';

  import type { Call, OutreachWorkflow, Partner } from '@stuut/api/models';
  import { CallSentiment, Direction } from '@stuut/api/models';
  import { CallQueryParams } from '@stuut/api/query';
  import { DirectionBadge } from '@ui/components/badge';
  import { Button } from '@ui/components/button';
  import { ComboSelect } from '@ui/components/combo-select';
  import { useLoader } from '@ui/components/combo-select/useLoader';
  import { RangeCalendar } from '@ui/components/range-calendar';
  import { toast } from '@ui/components/sonner';
  import { ModelTable, TablePagination } from '@ui/components/table';
  import { Tooltip, TooltipContent, TooltipTrigger } from '@ui/components/tooltip';
  import { cn } from '@ui/utils/utils';

  import { stuut } from '@/api';
  import { FilterShell } from '@/components/filter-groups';
  import { BusinessUnitFilter, CustomerFilter } from '@/components/filters';
  import { usePaginatedTable } from '@/composables';
  import { useBusinessUnits } from '@/composables/useBusinessUnits';
  import { useCallsList } from '@/composables/useCallsList';
  import { useProfile } from '@/store';
  import type { CallProcessingStep } from '@/types/callProcessing';
  import { downloadCSV } from '@/utils/csvDownload';

  import CallSentimentIcon from '../CallSentimentIcon.vue';

  import { createColumns } from './callColumns';

  defineProps<{
    partner?: Partner;
  }>();

  const emits = defineEmits<{
    (e: 'rowClick', row: Call): void;
  }>();

  const df = new DateFormatter('en-US', {
    dateStyle: 'medium',
  });

  interface ClassificationOption {
    label: string;
    value: string;
  }

  interface SentimentOption {
    label: string;
    value: CallSentiment;
  }

  const route = useRoute();
  const { hasBusinessUnits } = useBusinessUnits();
  const profileStore = useProfile();
  const callProcessingSteps = inject<Map<string, CallProcessingStep>>('callProcessingSteps');
  const newCallIds = inject<Ref<Set<string>>>('newCallIds', ref(new Set()));
  const { isUsingV2CallsList, listCalls } = useCallsList();

  const rowClass = (row: Row<Call>) => {
    if (newCallIds.value.has(row.original.id)) return 'animate-new-row-fade';
    return undefined;
  };

  const queryParams = ref<CallQueryParams>(
    CallQueryParams.init({
      defaults: {
        page: 0,
        pageSize: 50,
      },
      json: route.query,
    }),
  );

  const totalRecords = ref<number>();

  const queryClient = useQueryClient();

  onBeforeUnmount(() => {
    queryClient.removeQueries({ exact: false, queryKey: ['calls'] });
  });

  const query = useQuery<Call[], any>({
    initialData: [], // fallback / default value

    queryFn: async () => {
      try {
        const response = await listCalls(queryParams.value);

        totalRecords.value = response.total_records ?? undefined;
        return response.data;
      } catch (e) {
        console.error(e);
        return [];
      }
    },
    queryKey: ['calls', queryParams.value],
    refetchOnWindowFocus: false,
  });

  const loading = computed(() => query.isLoading.value || query.isFetching.value);
  const isExportDisabled = computed(
    () => totalRecords.value === undefined || totalRecords.value > 20000,
  );

  const isDownloadingReport = ref(false);

  const workflowLoader = useLoader({
    load: (args?) => stuut.outreachWorkflows.list(),
  });

  const toCalendarDate = (dayjsDate: dayjs.Dayjs): DateValue => {
    return new CalendarDate(dayjsDate.year(), dayjsDate.month() + 1, dayjsDate.date());
  };

  const selectedDirection = ref<Direction | null>(null);
  const selectedWorkflow = ref<OutreachWorkflow | null>(null);
  const selectedClassification = ref<ClassificationOption[]>([]);
  const selectedSentiment = ref<SentimentOption[]>([]);
  const dateRange = ref<DateRange>({
    end: queryParams.value.before ? toCalendarDate(queryParams.value.before) : undefined,
    start: queryParams.value.after ? toCalendarDate(queryParams.value.after) : undefined,
  });

  const columns = computed(() =>
    createColumns(callProcessingSteps, profileStore.profile.is_support_user),
  );

  const { table } = usePaginatedTable<Call, CallQueryParams>({
    params: queryParams as any,
    query,
    tableOptions: {
      get columns() {
        return columns.value;
      },
      getRowId: (row) => row.id,
    },
    useRouterQuery: true,
  });

  const classificationOptions: ClassificationOption[] = [
    { label: 'Account inquiry', value: 'account_inquiry' },
    { label: 'Cancellation request', value: 'cancellation_request' },
    { label: 'Complaint', value: 'complaint' },
    { label: 'Contact inquiry', value: 'contact_inquiry' },
    { label: 'Dispute', value: 'dispute' },
    { label: 'Feedback', value: 'feedback' },
    { label: 'Invoice inquiry', value: 'invoice_inquiry' },
    { label: 'Other', value: 'other' },
    { label: 'Payment inquiry', value: 'payment_inquiry' },
    { label: 'Payment promise', value: 'payment_promise' },
    { label: 'Refund request', value: 'refund_request' },
    { label: 'Support request', value: 'support_request' },
    { label: 'Wrong contact', value: 'wrong_contact' },
  ];

  const sentimentOptions: SentimentOption[] = Object.values(CallSentiment).map((sentiment) => ({
    label: changeCase.capitalCase(sentiment),
    value: sentiment,
  }));

  const routeValues = (value: unknown): string[] => {
    if (!value) return [];
    return (Array.isArray(value) ? value : [value]).filter(
      (queryValue): queryValue is string => typeof queryValue === 'string',
    );
  };

  onBeforeMount(async () => {
    if (route.query?.direction) {
      selectedDirection.value = route.query?.direction as Direction;
    }

    if (route.query?.workflow) {
      const workflowId = Array.isArray(route.query.workflow)
        ? route.query.workflow[0]
        : route.query.workflow;

      if (workflowId) {
        selectedWorkflow.value = await stuut.outreachWorkflows.retrieve(workflowId);
      }
    }

    if (route.query?.classification) {
      selectedClassification.value = routeValues(route.query.classification)
        .map((val) => classificationOptions?.find((option) => option.value === val))
        .filter((option) => option !== undefined) as ClassificationOption[];
    }

    if (route.query?.sentiment) {
      selectedSentiment.value = routeValues(route.query.sentiment)
        .map((val) => sentimentOptions.find((option) => option.value === val))
        .filter((option) => option !== undefined) as SentimentOption[];
    }
  });

  const downloadReport = async () => {
    if (isDownloadingReport.value) return;
    isDownloadingReport.value = true;
    try {
      const exportFilters = queryParams.value.serialize({
        forApiRequest: true,
      });
      delete exportFilters.page;
      delete exportFilters.limit;
      const response = await stuut.post('v1/calls/export', {
        data: exportFilters,
        headers: { Accept: 'text/csv' },
        responseType: 'blob',
      });

      downloadCSV(response.data, 'calls.csv');
    } catch (error: any) {
      console.error(error);
      toast.error(error.clientMessage || 'An error occurred');
    } finally {
      isDownloadingReport.value = false;
    }
  };
</script>

<template>
  <div class="grid grid-rows-[min-content_1fr_min-content] overflow-hidden h-full gap-y-3">
    <div class="flex flex-wrap gap-x-8 gap-y-2 justify-between items-center">
      <div class="flex flex-wrap items-center gap-2">
        <ComboSelect
          v-model="selectedDirection"
          clearable
          variant="filter"
          :options="[Direction.OUTBOUND, Direction.INBOUND]"
          @update:model-value="
            (v) => {
              Object.assign(queryParams, {
                direction: (v as Direction) || undefined,
              });
            }
          "
        >
          <template #filter-label>
            <i class="far fa-arrow-up-right text-xs" />
            <span> Direction </span>
          </template>

          <template #selected="{ value }">
            {{ changeCase.capitalCase(value) }}
          </template>

          <template #option="{ option }">
            <div class="flex items-center gap-2">
              <DirectionBadge :direction="option" />
              <span> {{ changeCase.capitalCase(option) }} </span>
            </div>
          </template>
        </ComboSelect>

        <CustomerFilter
          query-name="partner"
          @update="(partner) => Object.assign(queryParams, { page: 0, partner: partner?.id })"
        />

        <BusinessUnitFilter
          v-if="hasBusinessUnits"
          @update="(ids) => Object.assign(queryParams, { page: 0, business_unit_id__in: ids })"
        />

        <ComboSelect
          v-model="selectedClassification"
          :options="classificationOptions"
          searchable
          variant="filter"
          placeholder="Classifications"
          :multiple="true"
          search-placeholder="Filter by classifications..."
          option-identifier="label"
          @update:model-value="
            (v: ClassificationOption | ClassificationOption[] | null) => {
              Object.assign(queryParams, {
                classification: Array.isArray(v)
                  ? v?.map((c) => c.value)
                  : v
                    ? [v.value]
                    : undefined,
              });
            }
          "
        >
          <template #filter-label>
            <i class="fa-regular fa-tags text-xs" />
            <span> Classifications </span>
          </template>
        </ComboSelect>

        <ComboSelect
          v-if="isUsingV2CallsList"
          v-model="selectedSentiment"
          :options="sentimentOptions"
          searchable
          variant="filter"
          placeholder="Sentiment"
          :multiple="true"
          search-placeholder="Filter by sentiment..."
          option-identifier="label"
          @update:model-value="
            (v: SentimentOption | SentimentOption[] | null) => {
              Object.assign(queryParams, {
                sentiment: Array.isArray(v)
                  ? v?.map((sentiment) => sentiment.value)
                  : v
                    ? [v.value]
                    : undefined,
              });
            }
          "
        >
          <template #filter-label>
            <i class="fa-regular fa-face-smile text-xs" />
            <span> Sentiment </span>
          </template>

          <template #option="{ option }">
            <div class="flex items-center gap-2">
              <CallSentimentIcon :sentiment="option.value" />
              <span>{{ option.label }}</span>
            </div>
          </template>
        </ComboSelect>

        <ComboSelect
          v-model="selectedWorkflow"
          :loader="workflowLoader"
          clearable
          variant="filter"
          option-identifier="id"
          search-placeholder="Set customer..."
          @update:model-value="
            (v) => {
              Object.assign(queryParams, {
                workflow: (v as OutreachWorkflow | undefined)?.id,
              });
            }
          "
        >
          <template #filter-label>
            <i class="fas fa-diagram-nested text-xs" />
            <span> Workflow </span>
          </template>

          <template #selected="{ value }">
            {{ (value as unknown as OutreachWorkflow)?.name }}
          </template>

          <template #option="{ option }">
            {{ option.name }}
          </template>
        </ComboSelect>

        <FilterShell
          :hide-clear="!(queryParams.before || queryParams.after)"
          @clear="
            () => {
              Object.assign(dateRange, { start: undefined, end: undefined });
              Object.assign(queryParams, {
                before: undefined,
                after: undefined,
              });
            }
          "
          @open="
            (open) => {
              if (!open) {
                Object.assign(queryParams, {
                  before: dateRange.end && dayjs(dateRange.end.toString()),
                  after: dateRange.start && dayjs(dateRange.start.toString()),
                });
              }
            }
          "
        >
          <template #popover-trigger>
            <Button
              variant="outline"
              size="sm"
              :class="
                cn(
                  !dateRange && 'text-muted-foreground',
                  queryParams.before || queryParams.after
                    ? 'rounded-r-none border-r-0'
                    : 'rounded-r-sm border-r',
                )
              "
            >
              <i class="fas fa-calendar" />

              <span>
                <template v-if="dateRange.start">
                  <template v-if="dateRange.end">
                    {{ df.format(dateRange.start.toDate(getLocalTimeZone())) }}
                    -
                    {{ df.format(dateRange.end.toDate(getLocalTimeZone())) }}
                  </template>

                  <template v-else>
                    {{ df.format(dateRange.start.toDate(getLocalTimeZone())) }}
                  </template>
                </template>
                <template v-else>Date</template>
              </span>
            </Button>
          </template>

          <template #popover-content>
            <RangeCalendar
              v-model="dateRange as DateRange"
              class="p-2"
              @update:model-value="
                (mv) => {
                  if (!mv.start) {
                    Object.assign(dateRange, {
                      start: undefined,
                      end: undefined,
                    });
                  } else {
                    Object.assign(dateRange, mv);
                  }
                }
              "
              @update:start-value="
                (startDate) => {
                  if (!startDate) {
                    Object.assign(dateRange, {
                      start: undefined,
                      end: undefined,
                    });
                  } else if (dateRange.start && dateRange.end) {
                    Object.assign(dateRange, {
                      start: startDate,
                      end: undefined,
                    });
                  } else {
                    dateRange.start = startDate;
                  }
                }
              "
            />
          </template>
        </FilterShell>
      </div>

      <Tooltip>
        <TooltipTrigger>
          <Button
            size="sm"
            :disabled="isExportDisabled"
            :loading="isDownloadingReport"
            @click="downloadReport"
          >
            <i class="fas fa-download" />
            <span>Export</span>
          </Button>
        </TooltipTrigger>

        <TooltipContent>
          Download an export of the call activity invoices. Limit of 20,000 records per report.
        </TooltipContent>
      </Tooltip>
    </div>

    <ModelTable
      :columns
      :table
      :loading
      :row-class="rowClass"
      :no-search-result="!table.getRowModel().rows?.length"
      @row-click="(row) => emits('rowClick', row.original as Call)"
    />

    <TablePagination :table="table" :loading="loading" no-select :total-records />
  </div>
</template>
